import React from "react";
import { useFormContext } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { GetListParams, useDataProvider } from "@refinedev/core";
import { AlertCircleIcon } from "lucide-react";

import { RemoteMultiSelect } from "@/components/fields/remote-multi-select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export type IRemoteMultiSelectFieldCustomData = {
  getListParams: (query: string) => GetListParams;
  accessorFn: (option: any) => {
    id: string;
    name: string;
    iconUrl?: string | undefined;
    number?: string | undefined;
  };
  dataProviderName?: string;
  /** Map a stored value entry (string id) back into an option object so the
   * picker can render selected chips. If omitted we treat the value as
   * `{id: v, name: v}`. */
  toOption?: (value: any) => any;
};

export const RemoteMultiSelectField: React.FC<AutoFormFieldProps> = ({
  id,
  field,
  inputProps,
}) => {
  const form = useFormContext();
  const value: any[] = form.watch(id) ?? [];
  const customData = field.fieldConfig?.customData as
    | IRemoteMultiSelectFieldCustomData
    | undefined;
  const { getList } = useDataProvider()(customData?.dataProviderName);

  if (!customData) {
    return (
      <Alert variant="destructive" className="max-w-md">
        <AlertCircleIcon />
        <AlertTitle>Oops</AlertTitle>
        <AlertDescription>
          Add fetch function in customData in order to parse remoteMultiSelect
        </AlertDescription>
      </Alert>
    );
  }

  const toOption =
    customData.toOption ??
    ((v: any) => (typeof v === "string" ? { id: v, name: v } : v));

  return (
    <RemoteMultiSelect
      value={value.map(toOption)}
      showSelected
      triggerType="change"
      onValueChanged={(options) => {
        // Persist as a string[] of ids (matches the schema for `sites`,
        // `event_ids`, etc.). Callers that need full objects can change
        // toOption + accessorFn to round-trip differently.
        const ids = (options ?? []).map((o) => customData.accessorFn(o).id);
        form.setValue(id, ids);
        inputProps.onChange({ target: { name: id, value: ids } });
      }}
      accessorFn={customData.accessorFn}
      onSearch={async (query, onStatusChanged) => {
        onStatusChanged("loading");
        const response = await getList?.(customData.getListParams(query));
        onStatusChanged("success");
        return (response?.data ?? []) as any[];
      }}
    />
  );
};
